import { StyleSheet, css } from 'aphrodite';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Theme } from '../../config/theme';

const styles = StyleSheet.create({
  headerContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    minWidth: 0, // Allow text truncation
  },
  avatar: {
    width: '3rem',
    height: '3rem',
    borderRadius: '50%',
    backgroundColor: '#e0e0e0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    color: '#ffffff',
    flexShrink: 0,
    [Theme.mobile_query]: {
      width: '2.5rem',
      height: '2.5rem',
    },
  },
  avatarImage: {
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    objectFit: 'cover',
  },
  nameStatus: {
    marginLeft: '1rem',
    flex: 1,
    minWidth: 0, // Allow text truncation
    [Theme.mobile_query]: {
      marginLeft: '0.75rem',
    },
  },
  name: {
    margin: 0,
    fontSize: '1.125rem',
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    [Theme.mobile_query]: {
      fontSize: '1rem',
    },
  },
  status: {
    margin: 0,
    fontSize: '0.875rem',
    color: '#6c757d',
    [Theme.mobile_query]: {
      fontSize: '0.75rem',
    },
  },
  menuContainer: {
    position: 'relative',
  },
  menuButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '1.5rem',
    color: '#6c757d',
    padding: '0.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ':hover': {
      color: '#495057',
    },
    [Theme.mobile_query]: {
      padding: '0.75rem',
      minWidth: '44px',
      minHeight: '44px',
    },
  },
  menu: {
    position: 'absolute',
    top: '100%',
    right: 0,
    width: 240,
    backgroundColor: '#ffffff',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    borderRadius: '0.5rem',
    transformOrigin: 'top right',
    transition: 'all 200ms ease-out',
    opacity: 0,
    transform: 'scale(0.95)',
    pointerEvents: 'none',
    zIndex: 1000,
    border: '1px solid #e9ecef',
  },
  menuOpen: {
    opacity: 1,
    transform: 'scale(1)',
    pointerEvents: 'auto',
  },
  menuList: {
    margin: 0,
    padding: '0.25rem 0',
    listStyle: 'none',
  },
  menuItem: {
    display: 'block',
    width: '100%',
    padding: '0.75rem 1rem',
    fontSize: '0.875rem',
    color: '#495057',
    cursor: 'pointer',
    transition: 'background-color 150ms',
    textAlign: 'left',
    border: 'none',
    borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
    background: 'none',
    ':hover': {
      backgroundColor: '#f8f9fa',
    },
    ':last-child': {
      borderBottom: 'none',
    },
  },
  menuItemDanger: {
    color: '#dc3545',
    ':hover': {
      backgroundColor: '#fff5f5',
    },
  },
});

ConversationHeader.propTypes = {
  name: PropTypes.string.isRequired,
  status: PropTypes.string,
  image: PropTypes.string,
  onVisitProfile: PropTypes.func,
  onReportConversation: PropTypes.func,
  onUnfriend: PropTypes.func,
};

export function ConversationHeader({
  name,
  status,
  image,
  onVisitProfile,
  onReportConversation,
  onUnfriend,
}) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleClickOutside = e => {
    if (isMenuOpen && !e.target.closest(`.${css(styles.menuContainer)}`)) {
      setIsMenuOpen(false);
    }
  };

  React.useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isMenuOpen]);

  const initials = name
    .split(' ')
    .map(n => n[0])
    .join('');

  return (
    <div className={css(styles.headerContainer)}>
      <div className={css(styles.userInfo)}>
        <div className={css(styles.avatar)}>
          {image ? <img src={image} alt={name} className={css(styles.avatarImage)} /> : initials}
        </div>
        <div className={css(styles.nameStatus)}>
          <h2 className={css(styles.name)}>{name}</h2>
          <p className={css(styles.status)}>{status}</p>
        </div>
      </div>
      <div className={css(styles.menuContainer)}>
        <button
          type="button"
          className={css(styles.menuButton)}
          onClick={toggleMenu}
          aria-label="Menu"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="12" cy="6" r="2" fill="currentColor" />
            <circle cx="12" cy="12" r="2" fill="currentColor" />
            <circle cx="12" cy="18" r="2" fill="currentColor" />
          </svg>
        </button>
        <div className={css(styles.menu, isMenuOpen && styles.menuOpen)}>
          <ul className={css(styles.menuList)}>
            <li>
              <button
                type="button"
                className={css(styles.menuItem)}
                onClick={() => {
                  if (onVisitProfile) onVisitProfile();
                  setIsMenuOpen(false);
                }}
              >
                Visit profile
              </button>
            </li>
            <li>
              <button
                type="button"
                className={css(styles.menuItem)}
                onClick={() => {
                  if (onReportConversation) onReportConversation();
                  setIsMenuOpen(false);
                }}
              >
                Report conversation
              </button>
            </li>
            <li>
              <button
                type="button"
                className={css(styles.menuItem, styles.menuItemDanger)}
                onClick={() => {
                  if (onUnfriend) onUnfriend();
                  setIsMenuOpen(false);
                }}
              >
                Unfriend and end conversation
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}



// WEBPACK FOOTER //
// ./src/views/MessengerConversation/conversation-header.js